import { UPGRADES } from '../config/tuning';
import { VEHICLES, vehicleById, type VehicleDef, type VehicleId } from '../config/vehicles';
import { UPGRADE_IDS, currentLevels, nextCost, type Garage, type UpgradeId, type UpgradeLevels } from '../sim/upgrades';
import { t, tId, type StringKey } from './i18n';

function el<T extends HTMLElement = HTMLElement>(selector: string): T {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing element ${selector}`);
  return found;
}

interface CardDef {
  name: StringKey;
  icon: string;
  /** Effect text at a level (level 0 = stock). */
  effect: (level: number, vehicle: VehicleDef) => string;
}

const pct = (perLevel: number, level: number) => `+${Math.round(perLevel * level * 100)}%`;

const CARDS: Record<UpgradeId, CardDef> = {
  engine: {
    name: 'up.engine',
    icon: '<path d="M4 9h3l2-3h6l2 3h3v8h-3l-2 2H9l-2-2H4z"/><path class="cut" d="M10 11h4v4h-4z"/>',
    effect: (l) => (l === 0 ? t('up.stock-power') : t('up.power', { v: pct(UPGRADES.engine.accelerationPerLevel, l) })),
  },
  tyres: {
    name: 'up.tyres',
    icon: '<circle cx="12" cy="12" r="9"/><circle class="cut" cx="12" cy="12" r="4"/>',
    effect: (l) => (l === 0 ? t('up.stock-grip') : t('up.grip', { v: pct(UPGRADES.tyres.gripPerLevel, l) })),
  },
  suspension: {
    name: 'up.suspension',
    icon: '<path d="M7 3h10v3H7zM7 18h10v3H7z"/><path d="M8 7l8 2.5-8 2.5 8 2.5-8 2.5" fill="none" stroke="currentColor" stroke-width="2"/>',
    effect: (l) => (l === 0 ? t('up.stock-springs') : t('up.travel', { v: `+${Math.round(UPGRADES.suspension.travelPerLevel * l * 100)} cm` })),
  },
  tank: {
    name: 'up.tank',
    icon: '<path d="M6 6h9l3 3v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z"/><path d="M8 3h5v3H8z"/>',
    effect: (l, v) => t('up.tank-l', { v: Math.round(v.fuel.capacity * (1 + (UPGRADES.tank.capacityPerLevel / 100) * l)) }),
  },
};

/** The garage overlay: four upgrade cards and the wallet. Purchases are dispatched as intents; the game applies them. */
export class GarageScreen {
  private readonly overlay = el('#garage-overlay');
  private readonly wallet = el('#garage-wallet');
  private readonly grid = el('#garage-grid');
  private readonly buttons = new Map<UpgradeId, HTMLButtonElement>();
  onBuy: (id: UpgradeId) => void = () => {};
  onVehicle: (id: VehicleId) => void = () => {};
  private readonly vehicles = el('#vehicle-row');
  onClose: () => void = () => {};

  constructor() {
    for (const id of UPGRADE_IDS) {
      const card = document.createElement('article');
      card.className = 'upgrade-card';
      card.dataset.upgrade = id;
      card.innerHTML = `
        <svg class="upgrade-icon" viewBox="0 0 24 24" aria-hidden="true">${CARDS[id].icon}</svg>
        <h3></h3>
        <p class="upgrade-effect"></p>
        <div class="upgrade-pips" role="meter" aria-valuemin="0" aria-valuemax="${UPGRADES.maxLevel}"></div>
        <button class="button upgrade-buy" type="button"></button>`;
      const pips = card.querySelector('.upgrade-pips')!;
      for (let i = 0; i < UPGRADES.maxLevel; i++) pips.append(document.createElement('span'));
      const buy = card.querySelector<HTMLButtonElement>('.upgrade-buy')!;
      buy.addEventListener('click', () => this.onBuy(id));
      this.buttons.set(id, buy);
      this.grid.append(card);
    }
    for (const v of VEHICLES) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'vehicle-card';
      card.dataset.vehicle = v.id;
      card.innerHTML = `<b>${v.name}</b><small></small><span class="vehicle-state"></span>`;
      card.addEventListener('click', () => this.onVehicle(v.id));
      this.vehicles.append(card);
    }
    el('#garage-close').addEventListener('click', () => this.onClose());
  }

  get visible(): boolean {
    return !this.overlay.hidden;
  }

  show(garage: Garage): void {
    this.render(garage);
    this.overlay.hidden = false;
    el('#garage-close').focus();
  }

  hide(): void {
    this.overlay.hidden = true;
  }

  render(garage: Garage): void {
    this.wallet.textContent = String(garage.coins);
    const vehicle = vehicleById(garage.vehicle)!;
    for (const id of UPGRADE_IDS) this.renderCard(id, currentLevels(garage), garage.coins, vehicle);
    for (const card of this.vehicles.querySelectorAll<HTMLButtonElement>('.vehicle-card')) {
      const v = vehicleById(card.dataset.vehicle!)!;
      const owned = garage.owned.includes(v.id);
      const state = card.querySelector('.vehicle-state')!;
      card.classList.toggle('is-current', v.id === garage.vehicle);
      card.classList.toggle('is-owned', owned);
      card.querySelector('small')!.textContent = tId(`veh.${v.id}`, v.blurb);
      if (v.id === garage.vehicle) state.textContent = t('veh.driving');
      else if (owned) state.textContent = t('veh.drive');
      else state.innerHTML = `<svg class="coin-mini" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/></svg>${v.price}`;
      const locked = !owned && garage.coins < v.price;
      card.classList.toggle('is-locked', locked);
      card.setAttribute('aria-label', owned ? `${v.name}: ${v.id === garage.vehicle ? t('veh.driving') : t('veh.drive')}` : t('veh.buy-aria', { name: v.name, cost: v.price }));
    }
  }

  /** Brief pulse on a card after a purchase. */
  flash(id: UpgradeId): void {
    this.grid
      .querySelector(`[data-upgrade="${id}"]`)
      ?.animate([{ transform: 'scale(1.06)', borderColor: 'var(--coin)' }, { transform: 'scale(1)' }], { duration: 260, easing: 'ease-out' });
  }

  private renderCard(id: UpgradeId, levels: UpgradeLevels, coins: number, vehicle: VehicleDef): void {
    const card = this.grid.querySelector<HTMLElement>(`[data-upgrade="${id}"]`)!;
    const level = levels[id];
    const cost = nextCost(levels, id);
    const def = CARDS[id];
    card.querySelector('h3')!.textContent = t(def.name);
    card.querySelector('.upgrade-pips')!.setAttribute('aria-label', t('up.level', { name: t(def.name) }));
    card.querySelector('.upgrade-effect')!.textContent = cost === null ? def.effect(level, vehicle) : `${def.effect(level, vehicle)} → ${def.effect(level + 1, vehicle).replace(/^\S+\s/, '')}`;
    const pips = card.querySelector('.upgrade-pips')!;
    pips.setAttribute('aria-valuenow', String(level));
    pips.querySelectorAll('span').forEach((pip, i) => pip.classList.toggle('is-on', i < level));
    const buy = this.buttons.get(id)!;
    if (cost === null) {
      buy.textContent = t('up.max');
      buy.disabled = true;
      buy.setAttribute('aria-label', t('up.max-aria', { name: t(def.name) }));
    } else {
      buy.innerHTML = `<svg class="coin-mini" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10"/></svg>${cost}`;
      buy.disabled = coins < cost;
      buy.setAttribute('aria-label', t('up.buy-aria', { name: t(def.name), cost }));
    }
    card.classList.toggle('is-max', cost === null);
  }
}
